import { LabelCreateDialog } from "@/components/LabelCreateDialog";
import { LabelEditDialog } from "@/components/LabelEditDialog";
import { useDrawer } from "@/contexts/drawer-context";
import { useDeleteLabel, useLabels } from "@/hooks/use-labels";
import { useTodos } from "@/hooks/use-todos";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Inbox, Menu, MoreVertical, Plus, Tag } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/labels/")({
  component: LabelsIndexPage,
});

function LabelsIndexPage() {
  const { open: openDrawer } = useDrawer();
  const { data: labels, isLoading } = useLabels();
  const { data: unlabeledTodos } = useTodos("none");
  const deleteLabel = useDeleteLabel();
  const unlabeledCount = unlabeledTodos?.length ?? 0;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const handleDelete = (id: string, name: string) => {
    setMenuOpenId(null);
    if (window.confirm(`「${name}」を削除しますか？`)) {
      deleteLabel.mutate(id);
    }
  };

  const handleEdit = (id: string, name: string) => {
    setMenuOpenId(null);
    setEditTarget({ id, name });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ヘッダー */}
      <header className="flex items-center justify-between border-b px-4 py-3">
        <button
          type="button"
          onClick={openDrawer}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="メニュー"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">ラベル一覧</h1>
        <button
          type="button"
          onClick={() => setCreateDialogOpen(true)}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="ラベルを作成"
        >
          <Plus className="h-5 w-5" />
        </button>
      </header>

      {/* コンテンツ */}
      <div className="flex-1 overflow-y-auto px-4">
        {isLoading ? (
          <p className="py-8 text-center text-muted-foreground">
            読み込み中...
          </p>
        ) : !labels || labels.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <p className="text-muted-foreground">
              最初のラベルを作成しましょう
            </p>
            <button
              type="button"
              onClick={() => setCreateDialogOpen(true)}
              className="rounded-md bg-primary px-4 py-2 text-base font-medium text-primary-foreground"
            >
              ラベルを作成
            </button>
          </div>
        ) : (
          <ul>
            {labels.map((label) => (
              <li key={label.id} className="relative">
                <div className="flex items-center border-b">
                  <Link
                    to="/labels/$labelId"
                    params={{ labelId: label.id }}
                    className="flex flex-1 items-center gap-3 py-3"
                  >
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-base text-foreground">
                      {label.name}
                    </span>
                    {label.todoCount > 0 && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-sm text-muted-foreground">
                        {label.todoCount}
                      </span>
                    )}
                  </Link>
                  <button
                    type="button"
                    onClick={() =>
                      setMenuOpenId(menuOpenId === label.id ? null : label.id)
                    }
                    className="rounded-full p-2 text-muted-foreground hover:bg-muted"
                    aria-label="メニュー"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>

                {/* 三点メニュー ドロップダウン */}
                {menuOpenId === label.id && (
                  <div className="absolute right-0 top-10 z-10 w-32 rounded-md border bg-background shadow-md">
                    <button
                      type="button"
                      onClick={() => handleEdit(label.id, label.name)}
                      className="w-full px-4 py-2 text-left text-base text-foreground hover:bg-muted"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(label.id, label.name)}
                      className="w-full px-4 py-2 text-left text-base text-destructive hover:bg-muted"
                    >
                      削除
                    </button>
                  </div>
                )}
              </li>
            ))}

            {/* 未分類（ラベルなし TODO） */}
            <li>
              <div className="flex items-center border-b">
                <Link
                  to="/labels/$labelId"
                  params={{ labelId: "none" }}
                  className="flex flex-1 items-center gap-3 py-3"
                >
                  <Inbox className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-base text-foreground">
                    未分類
                  </span>
                  {unlabeledCount > 0 && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-sm text-muted-foreground">
                      {unlabeledCount}
                    </span>
                  )}
                </Link>
              </div>
            </li>
          </ul>
        )}
      </div>

      {/* ダイアログ */}
      <LabelCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
      {editTarget && (
        <LabelEditDialog
          open={editTarget !== null}
          onClose={() => setEditTarget(null)}
          labelId={editTarget.id}
          currentName={editTarget.name}
        />
      )}
    </div>
  );
}
